import React, { useEffect, useState } from 'react';
import { Button, FormField, Overlay, SelectField, Translate, hintStyle } from '../../components/purchasing';
import { createTransfer } from '../../services/transfer.service';
import { showApiError } from '../../services/apiError';

interface Option {
  id: string;
  name: string;
}

interface NewTransferDialogProps {
  open: boolean;
  phone: boolean;
  t: Translate;
  language: any;
  sites: Option[];
  reasons: Option[];
  defaultFromId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}

/** From, to and reason; the draft opens next so products can be added. */
const NewTransferDialog: React.FC<NewTransferDialogProps> = ({ open, phone, t, language, sites, reasons, defaultFromId, onClose, onCreated }) => {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const from = defaultFromId || (sites.length === 1 ? sites[0].id : '');
    setFromId(from);
    const others = sites.filter((site) => site.id !== from);
    setToId(others.length === 1 ? others[0].id : '');
    setReasonId('');
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    const nextErrors: Record<string, string> = {};
    if (!fromId) nextErrors.from = t('Choose the site the stock leaves');
    if (!toId) nextErrors.to = t('Choose the site the stock goes to');
    else if (toId === fromId) nextErrors.to = t('Choose a different site from From');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setBusy(true);
    try {
      const response: any = await createTransfer({
        // The API puts the signed-in company here.
        company_id: localStorage.getItem('companyId') || '',
        from_location_id: fromId,
        to_location_id: toId,
        reason_id: reasonId || null,
        status_id: 1,
        // Required on every row; the API replaces it with the sender when the transfer is sent.
        sent_by: localStorage.getItem('userId') || '',
        inventoryTransferDetail: [],
      });
      const id = response?.data?.data?.id;
      if (id) onCreated(id);
      else onClose();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', flexDirection: phone ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: phone ? '8px' : '12px' }}>
      <Button phone={phone} variant={phone ? 'text' : 'secondary'} onClick={onClose}>
        {t('Cancel')}
      </Button>
      <Button phone={phone} variant="primary" fullWidth={phone} disabled={busy} aria-busy={busy || undefined} onClick={create}>
        {busy ? t('Creating transfer…') : t('Create transfer')}
      </Button>
    </div>
  );

  const siteSelect = (key: 'from' | 'to', value: string, onChange: (id: string) => void, placeholder: string) => (
    <SelectField
      id={`pu-new-${key}`}
      phone={phone}
      invalid={!!errors[key]}
      aria-invalid={!!errors[key] || undefined}
      aria-describedby={errors[key] ? `pu-new-${key}-error` : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{placeholder}</option>
      {sites.map((site) => (
        <option key={site.id} value={site.id} disabled={key === 'to' && site.id === fromId}>
          {site.name}
        </option>
      ))}
    </SelectField>
  );

  return (
    <Overlay open={open} phone={phone} title={t('New transfer')} titleId="pu-new-transfer-title" closeLabel={t('Close')} onClose={onClose} footer={footer} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '4px' }}>
        <FormField id="pu-new-from" label={t('From')} error={errors.from}>
          {siteSelect(
            'from',
            fromId,
            (id) => {
              setFromId(id);
              if (id && id === toId) setToId('');
            },
            t('Choose a site'),
          )}
        </FormField>
        <FormField id="pu-new-to" label={t('To')} error={errors.to} hint={<p style={hintStyle}>{t('Stock moves when the receiving site receives the transfer.')}</p>}>
          {siteSelect('to', toId, setToId, t('Choose a site'))}
        </FormField>
        <FormField id="pu-new-reason" label={t('Reason')}>
          <SelectField id="pu-new-reason" phone={phone} value={reasonId} onChange={(event) => setReasonId(event.target.value)}>
            <option value="">{t('No reason')}</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </SelectField>
        </FormField>
      </div>
    </Overlay>
  );
};

export default NewTransferDialog;
